import { Injectable } from '@angular/core';
import { inject } from '@angular/core';
import { FiltersConfig } from '@dataclouder/ngx-core';
import { CoreKnowledgeService } from '@dataclouder/ngx-knowledge';

import { KnowledgeWordStateService } from './knowledge-words-state.service';
import { LangWord } from './lexicore.classes';

@Injectable({
  providedIn: 'root',
})
export class KnowledgeWordCoreService {
  private _lexicoreDict: Record<string, Record<string, LangWord>> = {};

  private coreKnowledgeService = inject(CoreKnowledgeService);
  private knowledgeWordStateService = inject(KnowledgeWordStateService);

  public async getLexicoreDict(lang: string): Promise<Record<string, LangWord>> {
    // Load Lexicore from IndexedDB if exists. or Query the Core and save in IndexedDB.
    if (this._lexicoreDict[lang]) {
      return this._lexicoreDict[lang];
    } else {
      const result = await this.coreKnowledgeService.getAllKnowledgeCoreDataAsObject(`words_${lang}`);
      if (Object.keys(result).length === 0) {
        // 🐖 Core is not in IndexedDB, get from User Knowledge to Query the Core.
        const knowledgeWordStateArray = await this.knowledgeWordStateService.getKnowledgeWordStateArray(lang);
        const lexicoreArray: string[] = knowledgeWordStateArray.map((word) => word.concept);

        const filters: FiltersConfig = {
          filters: {
            word: {
              $in: lexicoreArray,
            },
          },
        };

        const httpResult = await this.coreKnowledgeService.queryCoreKnowledge({ collection: `words_${lang}`, filters });

        await this.coreKnowledgeService.bulkAddOrUpdateKnowledgeCoreData(`words_${lang}`, httpResult);
        const lexicoreDict = await this.coreKnowledgeService.getAllKnowledgeCoreDataAsObject(`words_${lang}`);
        this._lexicoreDict[lang] = lexicoreDict;

        return lexicoreDict;
      } else {
        this._lexicoreDict[lang] = result;
        return result;
      }
    }
  }

  public async getWords(lang: string, words: string[]) {
    const lexicoreDict = await this.getLexicoreDict(lang);
    return words.map((word) => lexicoreDict[word]);
  }

  public async getWord(lang: string, word: string): Promise<LangWord> {
    const lexicoreDict = await this.getLexicoreDict(lang);
    return lexicoreDict[word];
  }

  public async updateMemoryAndDB(lang: string, words: LangWord[]) {
    this.coreKnowledgeService.bulkAddOrUpdateKnowledgeCoreData(`words_${lang}`, words);
    const lexicoreDict = await this.getLexicoreDict(lang);

    for (const word of words) {
      lexicoreDict[word.word] = word;
    }
  }

  public clearMemoryAndDB(): void {
    this.coreKnowledgeService.clearAllData();
    this._lexicoreDict = {};
  }
}
